import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { buildPermission } from "portal/utils/auth";
import { checkNever } from "common/utils/identity";
import { classes } from "portal/theme/theme";
import {
  CROP_CATEGORY_BASE_METRIC_KEYS,
  CROP_CATEGORY_THINNING_METRIC_KEYS,
  CropCategoryMetricKey,
  getCropCategoryMetricName,
  isDefaultSpatialSelection,
  isSpatialOneOf,
  SPATIAL_COVERAGE,
  SPATIAL_DEFAULT_SELECTION,
  SPATIAL_ENTRY_TIMING,
  SPATIAL_METRIC_GROUPS,
  SPATIAL_STATIC_WEED_TYPE_METRICS,
  SpatialMetricSelection,
} from "portal/utils/spatialMetrics";
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import {
  getSpatialMetricName,
  MetricValidation,
  SpatialMetric,
} from "portal/utils/metrics";
import {
  JigsawBlocksCrop,
  JigsawBlocksWeed,
} from "../../../../../../protos/golang/generated/ts2/portal/jigsaw";
import { JigsawRobotHistory } from "../../../views/robots/useRobotHistory";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { titleCase } from "portal/utils/strings";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import ExpandIcon from "@mui/icons-material/ExpandMoreOutlined";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import LayersIcon from "@mui/icons-material/LayersOutlined";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import React, {
  FunctionComponent,
  PropsWithChildren,
  ReactNode,
  useState,
} from "react";

interface Props {
  onClose: () => void;
  selection: SpatialMetricSelection;
  setSelection: (selection: SpatialMetricSelection) => void;
  // When set, the fixed-category weed layers and the static crop layers are
  // replaced by per-category ones; when undefined (legacy data / jigsaw off),
  // the pane renders exactly as before.
  jigsawBlocks?: JigsawRobotHistory;
  customerUuid?: string;
}

export const HeatmapPane: FunctionComponent<Props> = ({
  onClose,
  selection,
  setSelection,
  jigsawBlocks,
  customerUuid,
}) => {
  const { isEnabled: hasUnvalidatedMetrics } = useFeatureFlag(
    FeatureFlag.UNVALIDATED_METRICS
  );
  const { isEnabled: entryTimingEnabled } = useFeatureFlagForCustomer(
    FeatureFlag.ENTRY_TIMING,
    customerUuid
  );

  const canReadInternalMetrics = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.metrics_internal,
      PermissionDomain.all
    )
  );

  const { t } = useTranslation();

  const [openSection, setOpenSection] = useState<number>(() => {
    switch (selection.kind) {
      case "weedCategory": {
        return SPATIAL_METRIC_GROUPS.findIndex(
          (group) => group.key === "weeds"
        );
      }
      case "cropCategory": {
        return SPATIAL_METRIC_GROUPS.findIndex(
          (group) => group.key === "crops"
        );
      }
      case "static": {
        return SPATIAL_METRIC_GROUPS.findIndex((group) =>
          group.metrics.some((metric) => metric.id === selection.id)
        );
      }
      default: {
        checkNever(selection);
        return -1;
      }
    }
  });

  const isStaticSelected = (metric: SpatialMetric): boolean =>
    selection.kind === "static" && selection.id === metric.id;
  const selectStatic = (metric: SpatialMetric): void => {
    if (!isStaticSelected(metric)) {
      setSelection({ kind: "static", id: metric.id });
    }
  };

  return (
    <div
      className={classes(
        "absolute top-[10px] left-[50px] z-[3]",
        "text-black bg-white rounded",
        "max-h-full overflow-x-hidden overflow-y-auto",
        "cursor-default"
      )}
    >
      <div className="flex justify-end">
        <IconButton
          onClick={onClose}
          className="mr-1 text-black"
          edge="end"
          size="small"
          disableRipple
        >
          <ClearIcon className="text-sm" />
        </IconButton>
      </div>
      <div className="m-6 mt-0">
        <div className="flex items-center">
          <LayersIcon />
          <h3 className="mx-1 my-0">{t("components.map.heatmaps.name")}</h3>
          {!isDefaultSpatialSelection(selection) && (
            <Button
              color="secondary"
              className="ml-2 px-1 py-0"
              onClick={() => {
                setSelection(SPATIAL_DEFAULT_SELECTION);
                onClose();
              }}
              variant="outlined"
              size="small"
            >
              {titleCase(
                t("utils.actions.resetLong", {
                  subject: t("components.map.heatmaps.name"),
                })
              )}
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-1 mt-2">
          <Metric
            className="mt-2 ml-1"
            metric={SPATIAL_COVERAGE}
            selected={isStaticSelected(SPATIAL_COVERAGE)}
            onSelect={() => selectStatic(SPATIAL_COVERAGE)}
          />
          {SPATIAL_METRIC_GROUPS.map(
            ({ key, i18nKey: titleKey, internal, metrics }, index) => {
              if (internal && !canReadInternalMetrics) {
                return;
              }

              const isWeedsGroup = key === "weeds";
              const isCropsGroup = key === "crops";
              const showDynamicWeeds =
                jigsawBlocks !== undefined &&
                isWeedsGroup &&
                jigsawBlocks.weeds.length > 0;
              const showDynamicCrops =
                jigsawBlocks !== undefined &&
                isCropsGroup &&
                jigsawBlocks.crops.length > 0;

              const visibleMetrics = metrics.filter((metric) => {
                // don't show unmappable metrics
                if (!metric.canMap) {
                  return false;
                }
                // don't show unvalidated metrics
                if (
                  metric.validation === MetricValidation.PENDING &&
                  !hasUnvalidatedMetrics
                ) {
                  return false;
                }
                if (
                  metric.id === SPATIAL_ENTRY_TIMING.id &&
                  entryTimingEnabled !== true
                ) {
                  return false;
                }
                // don't show internal metrics
                if (
                  metric.validation === MetricValidation.INTERNAL &&
                  !canReadInternalMetrics
                ) {
                  return false;
                }
                // when per-category metrics are offered, they replace the
                // static crop metrics and fixed-category weed-type metrics;
                // otherwise (legacy-sourced metrics, or no categories in the
                // blocks) the static layers stay
                if (showDynamicCrops) {
                  return false;
                }
                if (
                  showDynamicWeeds &&
                  isSpatialOneOf(metric.id, SPATIAL_STATIC_WEED_TYPE_METRICS)
                ) {
                  return false;
                }
                return true;
              });
              if (
                visibleMetrics.length === 0 &&
                !showDynamicWeeds &&
                !showDynamicCrops
              ) {
                return;
              }

              return (
                <Accordion
                  disableGutters
                  elevation={0}
                  key={key}
                  expanded={index === openSection}
                  onChange={() => {
                    if (index === openSection) {
                      // close the section if we click it and it's already open
                      setOpenSection(-1);
                    } else {
                      setOpenSection(index);
                    }
                  }}
                  classes={{ root: "bg-white text-black" }}
                >
                  <AccordionSummary
                    className="font-bold mt-2"
                    expandIcon={<ExpandIcon className="text-black" />}
                  >
                    {titleCase(t(titleKey))}
                  </AccordionSummary>
                  <AccordionDetails className="flex flex-col">
                    {visibleMetrics.map((metric) => {
                      return (
                        <Metric
                          key={metric.id}
                          metric={metric}
                          selected={isStaticSelected(metric)}
                          onSelect={() => selectStatic(metric)}
                        />
                      );
                    })}
                    {showDynamicWeeds && (
                      <WeedTypeMetrics
                        categories={jigsawBlocks.weeds}
                        selection={selection}
                        setSelection={setSelection}
                      />
                    )}
                    {showDynamicCrops && (
                      <CropCategoryMetrics
                        categories={jigsawBlocks.crops}
                        selection={selection}
                        setSelection={setSelection}
                      />
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

interface CategorySelectionProps {
  selection: SpatialMetricSelection;
  setSelection: (selection: SpatialMetricSelection) => void;
}

// "Weed Type" radio; while it is the selected layer, a chip per weed category
// picks which category's fraction-of-total colors the map.
const WeedTypeMetrics: FunctionComponent<
  CategorySelectionProps & { categories: JigsawBlocksWeed[] }
> = ({ categories, selection, setSelection }) => {
  const { t } = useTranslation();

  const selectedCategoryId =
    selection.kind === "weedCategory" ? selection.categoryId : undefined;

  return (
    <>
      <RadioRow
        label={t("views.metrics.sections.weedType")}
        selected={selectedCategoryId !== undefined}
        onSelect={() => {
          if (selectedCategoryId === undefined && categories[0] !== undefined) {
            // default to the most prevalent category (they arrive sorted)
            setSelection({
              kind: "weedCategory",
              categoryId: categories[0].id,
            });
          }
        }}
      />
      {selectedCategoryId !== undefined && (
        <CategoryChips
          categories={categories}
          activeId={selectedCategoryId}
          onSelect={(id) =>
            setSelection({ kind: "weedCategory", categoryId: id })
          }
        />
      )}
    </>
  );
};

// A chip per crop category (always visible) picks which category the metric
// radios below apply to; thinning metrics appear only for categories with
// thinning activity somewhere on the map.
const CropCategoryMetrics: FunctionComponent<
  CategorySelectionProps & { categories: JigsawBlocksCrop[] }
> = ({ categories, selection, setSelection }) => {
  const { t } = useTranslation();

  const selectedCropMetric =
    selection.kind === "cropCategory" ? selection : undefined;
  // Which category the radios target when no crop layer is selected yet;
  // deliberately not persisted across layer switches.
  const [chosenCategoryId, setChosenCategoryId] = useState<
    string | undefined
  >();
  const activeCategoryId =
    selectedCropMetric?.categoryId ?? chosenCategoryId ?? categories[0]?.id;
  const activeCategory = categories.find(({ id }) => id === activeCategoryId);
  if (activeCategory === undefined) {
    return;
  }

  const selectedKey =
    selectedCropMetric?.categoryId === activeCategory.id
      ? selectedCropMetric.key
      : undefined;

  return (
    <>
      <SubHeader>{t("components.map.heatmaps.cropCategory")}</SubHeader>
      <CategoryChips
        categories={categories}
        activeId={activeCategory.id}
        onSelect={(id) => {
          setChosenCategoryId(id);
          if (
            selectedCropMetric !== undefined &&
            selectedCropMetric.categoryId !== id
          ) {
            // keep showing the same metric, pointed at the new category —
            // unless it's a thinning metric the new category doesn't have
            const hasThinning = categories.find(
              (category) => category.id === id
            )?.hasThinning;
            const key =
              !hasThinning &&
              (
                CROP_CATEGORY_THINNING_METRIC_KEYS as readonly string[]
              ).includes(selectedCropMetric.key)
                ? "density"
                : selectedCropMetric.key;
            setSelection({ kind: "cropCategory", categoryId: id, key });
          }
        }}
      />
      <CropMetricRadios
        keys={CROP_CATEGORY_BASE_METRIC_KEYS}
        categoryId={activeCategory.id}
        selectedKey={selectedKey}
        setSelection={setSelection}
      />
      {activeCategory.hasThinning && (
        <>
          <SubHeader className="uppercase">
            {t("utils.metrics.spatial.metrics.thinning")}
          </SubHeader>
          <CropMetricRadios
            keys={CROP_CATEGORY_THINNING_METRIC_KEYS}
            categoryId={activeCategory.id}
            selectedKey={selectedKey}
            setSelection={setSelection}
          />
        </>
      )}
    </>
  );
};

interface CropMetricRadiosProps {
  keys: readonly CropCategoryMetricKey[];
  categoryId: string;
  selectedKey: CropCategoryMetricKey | undefined;
  setSelection: (selection: SpatialMetricSelection) => void;
}

const CropMetricRadios: FunctionComponent<CropMetricRadiosProps> = ({
  keys,
  categoryId,
  selectedKey,
  setSelection,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {keys.map((key) => (
        <RadioRow
          key={key}
          label={getCropCategoryMetricName(t, key)}
          selected={selectedKey === key}
          onSelect={() =>
            setSelection({ kind: "cropCategory", categoryId, key })
          }
        />
      ))}
    </>
  );
};

const SubHeader: FunctionComponent<
  PropsWithChildren<{ className?: string }>
> = ({ className, children }) => (
  <Typography
    className={classes("font-bold text-xs mt-2 ml-1 opacity-70", className)}
  >
    {children}
  </Typography>
);

interface CategoryChipsProps {
  categories: { id: string; localizedName: string }[];
  activeId: string | undefined;
  onSelect: (id: string) => void;
}

// Not RadioChips: the pane forces black-on-white styling that RadioChips'
// theme-derived inline colors would fight with.
const CategoryChips: FunctionComponent<CategoryChipsProps> = ({
  categories,
  activeId,
  onSelect,
}) => (
  <div className="flex flex-wrap gap-2 ml-6 my-2 max-w-72">
    {categories.map(({ id, localizedName }) => (
      <Chip
        key={id}
        label={localizedName}
        size="small"
        variant="outlined"
        className={classes(
          "border-black",
          activeId === id ? "bg-black text-white" : "text-black"
        )}
        onClick={() => {
          if (activeId !== id) {
            onSelect(id);
          }
        }}
      />
    ))}
  </div>
);

interface RadioRowProps {
  className?: string;
  disabled?: boolean;
  label: ReactNode;
  onSelect: () => void;
  selected: boolean;
}

const RadioRow: FunctionComponent<RadioRowProps> = ({
  className,
  disabled = false,
  label,
  onSelect,
  selected,
}) => (
  <FormControlLabel
    className={classes(className, { "opacity-50": disabled })}
    disabled={disabled}
    label={<Typography variant="body1">{label}</Typography>}
    control={
      <Checkbox
        className="p-0 pr-2 ml-4"
        onChange={() => onSelect()}
        disabled={disabled}
        checked={selected}
        indeterminateIcon={
          <IndeterminateCheckBoxIcon className="text-black text-lg" />
        }
        icon={<RadioButtonUncheckedIcon className="text-black text-lg" />}
        checkedIcon={<RadioButtonCheckedIcon className="text-black text-lg" />}
        size="small"
        color="secondary"
      />
    }
  />
);

interface MetricProps {
  className?: string;
  disabled?: boolean;
  metric: SpatialMetric;
  onSelect: () => void;
  selected: boolean;
}

const Metric: FunctionComponent<MetricProps> = ({
  className,
  disabled = false,
  metric,
  onSelect,
  selected,
}) => {
  const { t } = useTranslation();

  return (
    <RadioRow
      className={className}
      disabled={disabled}
      label={getSpatialMetricName(t, metric)}
      onSelect={onSelect}
      selected={selected}
    />
  );
};
